import { useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Woordmerk } from './ui';
import { useAuth } from '../lib/auth';
import { initialen, toonNaam } from '../lib/format';
import { usePoule } from '../lib/poule';
import { useLeague, useMailTegoed, usePoules, useProfiles, useUitnodigingen } from '../lib/queries';
import { ligtBijMij } from './Akkoord';
import {
  IcoonBeker,
  IcoonBel,
  IcoonDashboard,
  IcoonGebruik,
  IcoonHistorie,
  IcoonHome,
  IcoonLogs,
  IcoonMenu,
  IcoonNieuw,
  IcoonPlus,
  IcoonPoule,
  IcoonRanglijst,
  IcoonSleutel,
  IcoonSpeler,
  IcoonSpelers,
  IcoonStatistiek
} from './icons';

interface NavItem {
  pad: string;
  ikoon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  /** Ontbreekt hij, dan mag iedereen die binnen is erbij. */
  permissie?: string;
}

const GROEPEN: { groep: string; items: NavItem[] }[] = [
  {
    groep: 'Competitie',
    items: [
      { pad: '/', ikoon: IcoonHome, label: 'Home' },
      /* Je eigen spelersprofiel: rating, prestaties, rangverdeling. De
         instellingen (naam, foto, gebruikersnaam) staan onder je eigen naam
         rechtsboven — dat is iets anders dan wat anderen van je zien. */
      { pad: '/mijn-profiel', ikoon: IcoonSpeler, label: 'Mijn profiel' },
      { pad: '/overzicht', ikoon: IcoonDashboard, label: 'Overzicht', permissie: 'pagina.dashboard' },
      { pad: '/ranglijst', ikoon: IcoonRanglijst, label: 'Ranglijst', permissie: 'pagina.ranglijst' },
      { pad: '/statistieken', ikoon: IcoonStatistiek, label: 'Statistieken', permissie: 'pagina.statistieken' },
      { pad: '/poules', ikoon: IcoonPoule, label: 'Poules', permissie: 'pagina.poules' }
    ]
  },
  {
    /* Uitslag invoeren staat bewust niet in de zijbalk: dat doe je met de knop
       op Home en in de balk hierboven, en twee ingangen naar hetzelfde scherm
       maakt het menu alleen maar langer. */
    groep: 'Spelen',
    items: [
      { pad: '/indelen', ikoon: IcoonNieuw, label: 'Indelen', permissie: 'pagina.nieuw' },
      { pad: '/toernooi', ikoon: IcoonBeker, label: 'Toernooi', permissie: 'pagina.toernooi' },
      { pad: '/historie', ikoon: IcoonHistorie, label: 'Historie', permissie: 'pagina.historie' }
    ]
  },
  {
    groep: 'Systeem',
    items: [
      { pad: '/gebruikers', ikoon: IcoonSpelers, label: 'Gebruikersbeheer', permissie: 'pagina.gebruikers' },
      { pad: '/rollen', ikoon: IcoonSleutel, label: 'Rollenbeheer', permissie: 'pagina.rollen' },
      { pad: '/logs', ikoon: IcoonLogs, label: 'Logs', permissie: 'pagina.logs' },
      { pad: '/gebruik', ikoon: IcoonGebruik, label: 'Gebruik', permissie: 'pagina.gebruik' }
    ]
  }
];

/** De titel in de balk komt uit dezelfde lijst, zodat hij nooit uit de pas loopt. */
function titelVoor(pad: string): string {
  if (pad === '/') return 'Home';
  if (pad === '/mijn-profiel') return 'Mijn profiel';
  if (pad.startsWith('/speler/')) return 'Speler';
  if (pad.startsWith('/poules/')) return 'Poule';
  if (pad.startsWith('/toernooi/')) return 'Toernooi';
  const alle = GROEPEN.flatMap((g) => g.items);
  return alle.find((i) => i.pad === pad)?.label ?? 'Foosrankr';
}

export default function Layout() {
  const { profile, mag, demo, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const locatie = useLocation();

  const zichtbaar = GROEPEN.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.permissie || mag(i.permissie))
  })).filter(
    (g) => g.items.length
  );

  const zijbalk = (
    <div className="flex h-full flex-col bg-veld-900 text-veld-100">
      <div className="border-b border-white/10 px-5 py-5">
        <p className="opschrift mb-2 text-veld-300">Tafelvoetbal</p>
        <Link to="/" onClick={() => setMenuOpen(false)} className="inline-block">
          <Woordmerk />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-5">
        {zichtbaar.map((g) => (
          <div key={g.groep} className="mb-6 last:mb-0">
            <div className="opschrift px-2 pb-2 text-veld-300/70">{g.groep}</div>
            <div className="flex flex-col">
              {g.items.map((it) => (
                <NavLink
                  key={it.pad}
                  to={it.pad}
                  end={it.pad === '/'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 border-l-2 px-3 py-2.5 text-[14px] transition-all duration-150 ${
                      isActive
                        ? 'border-signaal bg-white/10 font-semibold text-white'
                        : 'border-transparent text-veld-100/70 hover:bg-white/5 hover:pl-4 hover:text-white'
                    }`
                  }
                >
                  <it.ikoon />
                  {it.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {demo ? (
        <div className="border-t border-white/10 px-5 py-4">
          <p className="text-[11px] leading-relaxed text-veld-200/70">Demomodus — verzonnen data</p>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-[236px] shrink-0 md:block">{zijbalk}</aside>

      {menuOpen ? (
        <div className="fixed inset-0 z-50 flex md:hidden" onMouseDown={() => setMenuOpen(false)}>
          <div className="w-[236px]" onMouseDown={(e) => e.stopPropagation()}>
            {zijbalk}
          </div>
          <div className="flex-1 bg-black/50" />
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-krijt-200 bg-krijt-50/85 px-4 py-3 backdrop-blur md:px-8">
          <button
            onClick={() => setMenuOpen(true)}
            className="rounded-sm border border-krijt-300 px-2 py-1.5 text-krijt-700 md:hidden"
            aria-label="Menu openen"
          >
            <IcoonMenu className="!h-5 !w-5" />
          </button>

          <h1 className="kop truncate text-[19px]">{titelVoor(locatie.pathname)}</h1>

          <PouleKiezer />

          <div className="ml-auto flex items-center gap-1">
            {mag('pagina.nieuw') ? (
              <Link
                to="/nieuw"
                className="mr-1 inline-flex items-center gap-1.5 rounded-sm bg-veld-800 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-veld-900"
              >
                <IcoonPlus className="!h-4 !w-4" />
                <span className="hidden sm:inline">Uitslag</span>
              </Link>
            ) : null}
            <Meldingen />
            <ProfielMenu
              naam={profile?.name ?? '?'}
              rol={profile?.role ?? ''}
              foto={profile?.photo ?? ''}
              onUitloggen={() => void signOut()}
            />
          </div>
        </header>

        <main className="min-w-0 flex-1">
          <div className="mx-auto max-w-6xl px-4 pb-20 pt-7 md:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Alles wat je doet, doe je in één poule. Die keuze staat hier, met je
 * favoriet bovenaan; hij geldt voor de ranglijst, de statistieken, het
 * invoeren en de toernooien tegelijk.
 */
function PouleKiezer() {
  const { pouleId, poule, mijnPoules, favorieten, kies } = usePoule();
  const [open, setOpen] = useState(false);
  const doos = useRef<HTMLDivElement>(null);
  useBuitenKlik(doos, () => setOpen(false));

  if (mijnPoules.length === 0) return null;

  return (
    <div className="relative ml-3" ref={doos}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-krijt-300 bg-white px-2.5 py-1.5 text-sm transition hover:bg-krijt-50"
      >
        {favorieten.has(pouleId ?? '') ? <span className="text-signaal">★</span> : null}
        <span className="max-w-[160px] truncate font-semibold">{poule?.naam ?? 'Kies een poule'}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4 text-krijt-500">
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open ? (
        <div className="absolute left-0 z-40 mt-1 w-64 border border-krijt-300 bg-white shadow-lg">
          <div className="opschrift border-b border-krijt-200 px-3 py-2 text-krijt-500">Jouw poules</div>
          {mijnPoules.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                kies(p.id);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-krijt-100 ${
                p.id === pouleId ? 'font-semibold' : ''
              }`}
            >
              {favorieten.has(p.id) ? <span className="text-signaal">★</span> : <span className="w-3" />}
              <span className="truncate">{p.naam}</span>
            </button>
          ))}
          <Link
            to="/poules"
            onClick={() => setOpen(false)}
            className="block border-t border-krijt-200 bg-krijt-50 px-3 py-2 text-sm font-semibold text-veld-700 hover:bg-krijt-100"
          >
            Alle poules →
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/**
 * De bel telt wat er op je ligt te wachten: een uitslag die jij moet erkennen,
 * een poule waarvoor je bent uitgenodigd, en voor een beheerder de aanmeldingen
 * die nog beoordeeld moeten worden. Elke regel brengt je naar de plek waar je
 * er iets aan kunt doen.
 *
 * Hier gaat geen mail achteraan. Dat was er wel, maar een melding die je ziet
 * zodra je binnenkomt doet hetzelfde zonder iemands inbox te vullen.
 */
function Meldingen() {
  const { mag, profile } = useAuth();
  const { pouleId } = usePoule();
  const [open, setOpen] = useState(false);
  const profielen = useProfiles();
  const wachtenden = (profielen.data ?? []).filter((p) => p.status === 'aangevraagd');
  const doos = useRef<HTMLDivElement>(null);
  useBuitenKlik(doos, () => setOpen(false));

  /* Uitslagen die op jouw handtekening wachten; die zijn voor iedereen. */
  const { wachtend, zoekSpeler } = useLeague(pouleId);
  const bijMij = wachtend.filter((m) => ligtBijMij(m, profile?.id ?? ''));

  /* Poules waarvoor je bent uitgenodigd en nog niet hebt geantwoord. */
  const uitnodigingen = useUitnodigingen();
  const poules = usePoules();
  const uitgenodigd = (uitnodigingen.data ?? []).filter((u) => u.profielId === profile?.id && u.status === 'open');
  const pouleNaam = (id: string) => (poules.data ?? []).find((p) => p.id === id)?.naam ?? 'een poule';

  /* De daglimiet van Resend. Wie de mailkoppeling beheert hoort het te weten
     zodra hij vol is, niet pas als iemand klaagt dat er geen bericht kwam. */
  const tegoed = useMailTegoed();
  const mailVol = Boolean(tegoed.data && tegoed.data.limiet && tegoed.data.vandaag >= tegoed.data.limiet);

  const totaal =
    (mag('pagina.gebruikers') ? wachtenden.length : 0) + bijMij.length + uitgenodigd.length + (mailVol ? 1 : 0);

  if (!mag('pagina.gebruikers') && !totaal) return null;

  return (
    <div className="relative" ref={doos}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-sm p-2 text-krijt-600 transition hover:bg-krijt-100 hover:text-krijt-900"
        aria-label="Meldingen"
      >
        <IcoonBel />
        {totaal ? (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-kritiek px-1 text-[10px] font-bold text-white">
            {totaal}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-40 mt-1 w-72 border border-krijt-300 bg-white shadow-lg">
          <div className="opschrift border-b border-krijt-200 px-4 py-2.5 text-krijt-500">Meldingen</div>

          {mailVol ? (
            <Link
              to="/gebruik"
              onClick={() => setOpen(false)}
              className="block border-b border-krijt-200 bg-red-50 px-4 py-2.5 text-sm transition last:border-b-0 hover:bg-red-100"
            >
              <span className="block">
                <strong>Mail geblokkeerd</strong> — de daglimiet van {tegoed.data?.limiet} is bereikt
              </span>
              <span className="mt-0.5 block text-xs font-semibold text-veld-700">Bekijken op Gebruik →</span>
            </Link>
          ) : null}

          {/* Elke regel is zelf de weg erheen; een lijstje dat alleen meldt en je
              daarna laat zoeken is half werk. */}
          {bijMij.length ? (
            <>
              {bijMij.slice(0, 5).map((m) => (
                <Link
                  key={m.id}
                  to="/"
                  onClick={() => setOpen(false)}
                  className="block border-b border-krijt-200 px-4 py-2.5 text-sm transition last:border-b-0 hover:bg-krijt-50"
                >
                  <span className="block">
                    <strong>{m.teamA.map((id) => toonNaam(zoekSpeler(id))).join(' & ')}</strong> {m.scoreA}–{m.scoreB}{' '}
                    <strong>{m.teamB.map((id) => toonNaam(zoekSpeler(id))).join(' & ')}</strong>
                  </span>
                  <span className="mt-0.5 block text-xs font-semibold text-veld-700">
                    Klopt deze stand? Bevestigen →
                  </span>
                </Link>
              ))}
            </>
          ) : null}

          {uitgenodigd.length ? (
            <>
              {uitgenodigd.slice(0, 5).map((u) => (
                <Link
                  key={u.id}
                  to="/poules"
                  onClick={() => setOpen(false)}
                  className="block border-b border-krijt-200 px-4 py-2.5 text-sm transition last:border-b-0 hover:bg-krijt-50"
                >
                  <span className="block">
                    Uitnodiging voor <strong>{pouleNaam(u.pouleId)}</strong>
                  </span>
                  <span className="mt-0.5 block text-xs font-semibold text-veld-700">Meedoen of afslaan →</span>
                </Link>
              ))}
            </>
          ) : null}

          {mag('pagina.gebruikers') && wachtenden.length ? (
            <>
              {wachtenden.slice(0, 5).map((p) => (
                <Link
                  key={p.id}
                  to="/gebruikers"
                  onClick={() => setOpen(false)}
                  className="block border-b border-krijt-200 px-4 py-2.5 text-sm transition last:border-b-0 hover:bg-krijt-50"
                >
                  <span className="block">
                    <strong>{p.name}</strong>
                    {p.username ? <span className="text-krijt-500"> @{p.username}</span> : null}
                  </span>
                  <span className="mt-0.5 block text-xs font-semibold text-veld-700">
                    Wacht op goedkeuring. Beoordelen →
                  </span>
                </Link>
              ))}
              <Link
                to="/gebruikers"
                onClick={() => setOpen(false)}
                className="block bg-krijt-50 px-4 py-2.5 text-sm font-semibold text-veld-700 hover:bg-krijt-100"
              >
                Naar gebruikersbeheer →
              </Link>
            </>
          ) : null}

          {!totaal ? <p className="px-4 py-5 text-center text-sm text-krijt-500">Niets dat op je wacht.</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function ProfielMenu({
  naam,
  rol,
  foto,
  onUitloggen
}: {
  naam: string;
  rol: string;
  foto: string;
  onUitloggen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const doos = useRef<HTMLDivElement>(null);
  useBuitenKlik(doos, () => setOpen(false));

  return (
    <div className="relative" ref={doos}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-sm py-1 pl-1 pr-2 transition hover:bg-krijt-100"
      >
        {foto ? (
          <img
            src={foto}
            alt=""
            className="h-8 w-8 shrink-0 rounded-full border border-black/10 object-cover"
          />
        ) : (
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-krijt-950 text-[12px] font-bold text-white">
            {initialen(naam)}
          </span>
        )}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4 text-krijt-500">
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-40 mt-1 w-56 border border-krijt-300 bg-white shadow-lg">
          <div className="border-b border-krijt-200 px-4 py-3">
            <p className="font-semibold">{naam}</p>
            <p className="opschrift mt-0.5 text-krijt-500">{rol}</p>
          </div>
          <Link
            to="/profiel"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm hover:bg-krijt-100"
          >
            Profielinstellingen
          </Link>
          <button
            onClick={onUitloggen}
            className="block w-full px-4 py-2.5 text-left text-sm text-kritiek hover:bg-krijt-100"
          >
            Uitloggen
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Sluit een uitklapmenu zodra er ergens anders geklikt wordt. */
function useBuitenKlik(doos: React.RefObject<HTMLElement | null>, sluit: () => void) {
  useEffect(() => {
    const opKlik = (e: MouseEvent) => {
      if (doos.current && !doos.current.contains(e.target as Node)) sluit();
    };
    document.addEventListener('mousedown', opKlik);
    return () => document.removeEventListener('mousedown', opKlik);
  }, [doos, sluit]);
}
